import { useEffect } from 'react';
import { useMachineStore } from '@/store/store';

/**
 * Subscribes to electron-updater IPC events and mirrors them into the store.
 * Headless — UI is rendered by the header indicator and SettingsModal.
 *
 * In browser dev mode (no Electron) this is a no-op.
 */
export function useAutoUpdater(): void {
  const setUpdateStatus = useMachineStore((s) => s.setUpdateStatus);
  const setDownloadProgress = useMachineStore((s) => s.setDownloadProgress);
  const setUpdateVersion = useMachineStore((s) => s.setUpdateVersion);
  const setAppVersion = useMachineStore((s) => s.setAppVersion);
  const setUpdateError = useMachineStore((s) => s.setUpdateError);

  useEffect(() => {
    const api = window.electronAPI?.updater;
    if (!api) return; // Not running in Electron (browser dev mode)

    void api.getAppVersion().then((version) => setAppVersion(version));

    api.onUpdateAvailable((info) => {
      setUpdateError(null);
      setUpdateVersion(info.version);
      setUpdateStatus('available');
    });

    api.onUpdateNotAvailable(() => {
      setUpdateStatus('idle');
    });

    api.onDownloadProgress((prog) => {
      setDownloadProgress(Math.round(prog.percent));
    });

    api.onUpdateDownloaded(() => {
      setUpdateStatus('ready');
      setDownloadProgress(100);
    });

    api.onUpdateError((message) => {
      setUpdateError(message);
      setUpdateStatus('error');
    });

    return () => {
      api.removeAllUpdateListeners();
    };
  }, [setUpdateStatus, setDownloadProgress, setUpdateVersion, setAppVersion, setUpdateError]);
}
